// LEGACY — superseded by features/client-sites/api/. Scheduled for deletion in 2 sprints.
import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import { ClientSiteItem } from "../interfaces/ClientSite";
import { ClientSitesResponse } from "../slices/clientSites/types";
import { DEFAULT_PAGE_SIZE } from "../common/constants";
import axios from "axios";
import config from "../config";

const api = new APIClient();
const { api: apiConfig } = config;

// Fixed New Zealand UUID
const NEW_ZEALAND_COUNTRY_ID = "228c2ad0-d0fb-4231-8260-1495863ed1c6";

// Helper function to get auth token from Redux Persist
const getAuthToken = () => {
  try {
    const persistedState = sessionStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.Login) {
        const loginState = JSON.parse(parsed.Login);
        if (loginState.user && Object.keys(loginState.user).length > 0) {
          return loginState.user.token || null;
        }
      }
    }
  } catch (e) {
    console.error("Error reading auth token from Redux Persist:", e);
  }
  return null;
};

// Check if we should use the fake backend
export const getAllClientSites = (
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<ClientSitesResponse> => {
  
  // Real API call with query parameters - typed response
  return api.get<ClientSitesResponse>(url.GET_CLIENT_SITES, {
    pageNumber,
    pageSize,
  });
};

export const getClientSitesByClientId = (
  clientId: string,
  pageNumber: number = 1,
  pageSize: number = 200
): Promise<ClientSitesResponse> => {
  // API call with clientId filter
  return api.get<ClientSitesResponse>(url.GET_CLIENT_SITES, {
    clientId,
    pageNumber,
    pageSize,
  });
};

/**
 * Get a single ClientSite by ID
 * @param id - ClientSite ID
 * @param withAttachment - Whether to include attachments (defaults to false)
 * @returns Promise with ClientSite item
 */
export const getClientSiteById = (
  id: string,
  withAttachment: boolean = false
): Promise<ClientSiteItem> => {
  const params: any = {};
  if (withAttachment) {
    params.withAttachment = true;
  }
  return api.get<ClientSiteItem>(`${url.GET_CLIENT_SITE_BY_ID}/${id}`, params);
};

export const createClientSite = (data: Partial<ClientSiteItem>): Promise<ClientSiteItem> => {
  
  // Clean payload for backend - only required fields
  // Always use fixed New Zealand country ID
  const payload: any = {
    clientId: data.clientId,
    tenantId: data.tenantId,
    siteName: data.siteName,
    address1: data.address1,
    address2: data.address2,
    countryId: NEW_ZEALAND_COUNTRY_ID,
    latitude: data.latitude || 0,
    longitude: data.longitude || 0,
    siteRadiusMeters: data.siteRadiusMeters || 200,
    requireGeofencing: data.requireGeofencing || false,
  };
  
  // Add siteTypeId if provided (not empty string)
  if (data.siteTypeId && data.siteTypeId.trim() !== "") {
    payload.siteTypeId = data.siteTypeId;
  }
  
  return api.create<ClientSiteItem>(url.ADD_NEW_CLIENT_SITE, payload);
};

/**
 * Create a new ClientSite with attachments
 * @param payload - ClientSite create payload
 * @param attachments - Array of File objects to attach
 * @returns Promise with created ClientSite item
 */
export const createClientSiteWithAttachments = (
  payload: Partial<ClientSiteItem>,
  attachments: File[]
): Promise<ClientSiteItem> => {
  const token = getAuthToken();
  const formData = new FormData();

  // Append all ClientSite fields
  if (payload.tenantId) formData.append("TenantId", payload.tenantId);
  if (payload.clientId) formData.append("ClientId", payload.clientId);
  if (payload.siteName) formData.append("SiteName", payload.siteName);
  if (payload.address1) formData.append("Address1", payload.address1);
  if (payload.address2) formData.append("Address2", payload.address2 || "");
  // Always use fixed New Zealand country ID
  formData.append("CountryId", NEW_ZEALAND_COUNTRY_ID);
  formData.append("Latitude", String(payload.latitude || 0));
  formData.append("Longitude", String(payload.longitude || 0));
  formData.append("SiteRadiusMeters", String(payload.siteRadiusMeters || 200));
  formData.append("RequireGeofencing", String(payload.requireGeofencing || false));
  
  // Add siteTypeId if provided (not empty string)
  if (payload.siteTypeId && payload.siteTypeId.trim() !== "") {
    formData.append("SiteTypeId", payload.siteTypeId);
  }

  // Append attachments (multiple files)
  attachments.forEach((file: File) => {
    formData.append("Attachments", file);
  });

  return axios.post<ClientSiteItem>(
    `${apiConfig.API_URL}${url.CREATE_CLIENT_SITE_WITH_ATTACHMENTS}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for file upload
    }
  ).then((response: any) => {
    const responseData = response.data || response;
    // Handle API response wrapper - response.result contains the ClientSite
    if (responseData?.result) {
      return responseData.result as ClientSiteItem;
    }
    return responseData as ClientSiteItem;
  });
};

export const updateClientSite = (
  id: string,
  data: Partial<ClientSiteItem>
): Promise<ClientSiteItem> => {
  
  // PUT payload - exclude clientId and tenantId
  // Always use fixed New Zealand country ID
  const payload: any = {
    siteName: data.siteName,
    address1: data.address1,
    address2: data.address2,
    countryId: NEW_ZEALAND_COUNTRY_ID,
    latitude: data.latitude || 0,
    longitude: data.longitude || 0,
    siteRadiusMeters: data.siteRadiusMeters || 200,
    requireGeofencing: data.requireGeofencing || false,
  };
  
  // Add siteTypeId if provided (can be null to clear it, or a valid ID)
  if (data.siteTypeId !== undefined && data.siteTypeId !== null && data.siteTypeId !== "") {
    payload.siteTypeId = data.siteTypeId;
  } else if (data.siteTypeId === null || data.siteTypeId === "") {
    // Allow clearing siteTypeId by setting to null
    payload.siteTypeId = null;
  }
  
  return api.put<ClientSiteItem>(url.UPDATE_CLIENT_SITE + "/" + id, payload);
};

/**
 * Update an existing ClientSite with attachments
 * @param id - ClientSite ID
 * @param payload - ClientSite update payload
 * @param attachments - Array of File objects to attach
 * @returns Promise with updated ClientSite item
 */
export const updateClientSiteWithAttachments = (
  id: string,
  payload: Partial<ClientSiteItem>,
  attachments: File[]
): Promise<ClientSiteItem> => {
  const token = getAuthToken();
  const formData = new FormData();

  // Append all ClientSite fields
  if (payload.siteName) formData.append("SiteName", payload.siteName);
  if (payload.address1) formData.append("Address1", payload.address1);
  if (payload.address2) formData.append("Address2", payload.address2 || "");
  // Always use fixed New Zealand country ID
  formData.append("CountryId", NEW_ZEALAND_COUNTRY_ID);
  formData.append("Latitude", String(payload.latitude || 0));
  formData.append("Longitude", String(payload.longitude || 0));
  formData.append("SiteRadiusMeters", String(payload.siteRadiusMeters || 200));
  formData.append("RequireGeofencing", String(payload.requireGeofencing || false));
  
  // Add siteTypeId if provided (can be null to clear it, or a valid ID)
  if (payload.siteTypeId !== undefined && payload.siteTypeId !== null && payload.siteTypeId !== "") {
    formData.append("SiteTypeId", payload.siteTypeId);
  } else if (payload.siteTypeId === null || payload.siteTypeId === "") {
    // Allow clearing siteTypeId by setting empty string
    formData.append("SiteTypeId", "");
  }

  // Append attachments (multiple files)
  console.log(`[updateClientSiteWithAttachments] Appending ${attachments.length} files`);
  attachments.forEach((file: File, index: number) => {
    console.log(`[updateClientSiteWithAttachments] File ${index + 1}: ${file.name}, size: ${file.size} bytes`);
    formData.append("Attachments", file);
  });
  
  // Debug: Log FormData entries
  console.log("[updateClientSiteWithAttachments] FormData entries:");
  for (const pair of formData.entries()) {
    if (pair[1] instanceof File) {
      console.log(`  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes)`);
    } else {
      console.log(`  ${pair[0]}: ${pair[1]}`);
    }
  }

  return axios.put<ClientSiteItem>(
    `${apiConfig.API_URL}${url.UPDATE_CLIENT_SITE_WITH_ATTACHMENTS}/${id}/WithAttachments`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
      timeout: 60000, // 60 seconds for file upload
    }
  ).then((response: any) => {
    const responseData = response.data || response;
    // Handle API response wrapper - response.result contains the ClientSite
    if (responseData?.result) {
      return responseData.result as ClientSiteItem;
    }
    return responseData as ClientSiteItem;
  });
};

export const deleteClientSite = (id: string): Promise<void> => {
  
  return api.delete<void>(url.DELETE_CLIENT_SITE + "/" + id);
};
